// src/modules/app-views/crm/pages/Boards/modals/QuickCaptureModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import agendasService from 'modules/app-views/crm/agendasService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import dayjs from 'dayjs';
import Step1Contact from './Step1Contact';
import Step2Opportunity from './Step2Opportunity';
import Step3NextAction from './Step3NextAction';

// *******************************************************************************
// THEME WRAPPER COMPONENT
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const QuickCaptureModal = ({ show, setShow, afterClose }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // GenericModal passes { form, validateForm, goFocus, setLoading } to onFinish
  const handleFinish = async ({ form }) => {
    const values = form?.getFieldsValue(true) || {};

    // Extract contact data (Step 1)
    const contactData = {
      name: (values.name || '').trim(),
      role: (values.role || '').trim(),
      company: (values.company || '').trim(),
      sector: (values.sector || '').trim(),
      phone: values.phone,
      email: values.email,
      note: (values.note || '').trim(),
    };

    // Extract opportunity data (Step 2)
    const opportunityData = {
      opportunity: (values.opportunity || '').trim(),
      opportunityType: values.opportunityType || 'sale',
      boardUid: values.boardUid,
      temperatureUid: values.temperatureUid,
      opportunitySource: values.opportunitySource,
      approximateValue: values.approximateValue,
    };

    // Extract next action data (Step 3)
    const nextActionData = {
      createEvent: !!values.createEvent,
      eventDate: values.eventDate,
      eventType: values.eventType || 'reminder',
      eventTitle: (values.eventTitle || '').trim(),
    };

    // 1. Create entity (contact person)
    let entityId = null;
    if (contactData.name) {
      const entityPayload = buildEntityPayload(contactData);
      const [createdEntity] = await axiosService.post({
        url: API.ENTITIES.URL.replace(':_id?', ''),
        data: entityPayload,
        returnAsObject: true,
      });
      entityId = createdEntity?._id || null;
    }

    // 2. Create opportunity
    const crmPayload = {
      ...opportunityData,
      status: 'backlog',
      entityId,
    };

    const [createdOpportunity] = await axiosService.post({
      url: API.CRM.URL.replace(':_id?', ''),
      data: crmPayload,
      returnAsObject: true,
    });

    const opportunityId = createdOpportunity?._id || null;

    // 3. Create event in agenda (if requested)
    if (nextActionData.createEvent && nextActionData.eventDate) {
      await agendasService.save({
        title: nextActionData.eventTitle || opportunityData.opportunity,
        date: dayjs.isDayjs(nextActionData.eventDate)
          ? nextActionData.eventDate.toISOString()
          : nextActionData.eventDate,
        eventType: nextActionData.eventType,
        linkedOpportunityId: opportunityId,
      });
    }

    appService.message({ flag: 's', text: 'quick_capture_success' });
    // Return undefined (not false) so GenericModal closes the modal automatically
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = [
    {
      stepTitle: 'contact',
      content: () => <Step1Contact />,
    },
    {
      stepTitle: 'opportunity',
      content: () => <Step2Opportunity />,
    },
    {
      stepTitle: 'next_action',
      content: () => <Step3NextAction />,
    },
  ];

  const initialValues = {
    opportunityType: 'sale',
    createEvent: false,
  };

  const requiredFields = ['name', 'opportunity'];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      title={translateX('quick_capture')}
      icon={<UserAddOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      requiredFields={requiredFields}
      width={560}
    />
  );
};

// *******************************************************************************
// EXPORT (named - for reuse as React component)
// *******************************************************************************
export { QuickCaptureModal };

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showQuickCaptureModal({ onSuccess } = {}) {
  const container = document.createElement('div');
  container.id = 'quick-capture-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);

  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <QuickCaptureModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
}

export default showQuickCaptureModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildEntityPayload(contactData) {
  const payload = {
    name: contactData.name,
    entityProfile: ['contact'],
    entityType: 'person',
  };

  // Add role as position/occupation if provided
  if (contactData.role) {
    payload.position = contactData.role;
  }

  // Add sector if provided
  if (contactData.sector) {
    payload.sector = contactData.sector;
  }

  // Add note if provided
  if (contactData.note) {
    payload.note = contactData.note;
  }

  // Add phone contact if provided
  if (contactData.phone?.value) {
    payload.phone = {
      value: contactData.phone.value,
      countryCode: contactData.phone.countryCode || 'BR',
      type: contactData.phone.type || 'mobile',
    };
  }

  // Add email contact if provided
  if (contactData.email?.value) {
    payload.email = {
      value: contactData.email.value,
      countryCode: 'BR',
    };
  }

  return payload;
}
